import React, { Suspense, lazy } from 'react'
import * as Immutable from 'immutable'
import { State, hasLoadedWithoutError } from 'rwwa-data-access'
import { DogColourGroup } from '@core/Areas/Racing/Components/RacingForm/Components/Common/DogColour'
import {
  GiddyUpAcceptors,
  IAcceptorRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpAcceptors'
import {
  GiddyUpRaces,
  IRaceRecord,
} from '@core/Areas/Racing/Components/RacingForm/data-access/giddyUpRaces'
import { trackGiddyUpShowSpeedMap } from '@core/Areas/Racing/Components/RacingForm/helpers/analytics'
import { getAcceptor } from '@core/Areas/Racing/Components/RacingForm/integration'
import { observe } from '@core/Components/HOCs/observe'
import { SpeedMapWrapperStyled } from '../Common/GiddyUp.styles'
import {
  chevronPath,
  jumpBgPoints,
  jumpLeftPath,
  jumpRightPath,
  jumpStraightPath,
} from '../Common/Paths/raw'

const DogPath = lazy(() => import('../Common/Paths/Lazy/DogPath'))

const numberOfLanes = 8
const laneHeight = 60
const viewWidth = 740
const viewX = -675
const dogWidth = 140
const boxWidth = 40
const jumDirectionWidth = 40
const jumpDirectionPadding = 5
const adjustedViewWidth = -viewX - boxWidth - jumDirectionWidth - jumpDirectionPadding
const nameY = 95

const DogSpeedMapComponent: React.FC<{
  raceState: State<IRaceRecord>
  acceptorState: State<IAcceptorRecord>
  raceKey: string
}> = ({ raceState, acceptorState, raceKey }) => {
  const race = GiddyUpRaces.getFrom(raceState, raceKey)

  React.useEffect(trackGiddyUpShowSpeedMap, [])

  if (!hasLoadedWithoutError(race)) {
    return <div />
  }

  const acceptors = race.sortedAcceptorKeys
    .map(starterKey => !!starterKey && GiddyUpAcceptors.getFrom(acceptorState, starterKey))
    .filter(
      a => !!a && hasLoadedWithoutError(a) && a.startingPosition > 0 && a.startingPosition < 9
    )
    // @ts-expect-error type issue to fixed later
    .sortBy((a: IAcceptorRecord) => a.isGiddyupScratched)
    // @ts-expect-error type issue to fixed later
    .groupBy((a: IAcceptorRecord) => a.startingPosition)
    // @ts-expect-error type issue to fixed later
    .map((value: Immutable.Iterable<number, IAcceptorRecord>) => value.first())

  return (
    <SpeedMapWrapperStyled data-tid-speed-map=''>
      <VisualMap acceptors={acceptors as Immutable.Iterable<number, IAcceptorRecord>} />
    </SpeedMapWrapperStyled>
  )
}

const state$ = Rx.Observable.combineLatest(
  GiddyUpRaces.data$,
  GiddyUpAcceptors.data$,
  (raceState, acceptorState) => ({ raceState, acceptorState })
)

export const DogSpeedMap = observe<
  { raceState: State<IRaceRecord>; acceptorState: State<IAcceptorRecord> },
  { raceKey: string }
>(state$, DogSpeedMapComponent)

// ================
// Local Components
// ================

const VisualMap: React.FC<{ acceptors: Immutable.Iterable<number, IAcceptorRecord> }> = ({
  acceptors,
}) => {
  const railHeight = 110
  const bottomPadding = 20

  return (
    <div className='dogsMap'>
      <svg
        viewBox={`${viewX},0, ${viewWidth}, ${railHeight + numberOfLanes * laneHeight + bottomPadding}`}
        className='visualMap'
      >
        <g transform='translate(0, 25)'>
          <g>
            <text className='firstSection' transform='translate(-10, 10)' textAnchor='middle'>
              1st Section
            </text>
            <line className='rail' x1='-720' y1='29' x2='60' y2='29' />

            {Array(7)
              .fill(0)
              .map((x, i) => {
                const left = -(i * 95) - 5
                const top = 30
                return (
                  <g key={i}>
                    <path
                      className='rail'
                      transform={`translate(${left},${top})`}
                      d='M0,0l5,5l0,25'
                    />
                  </g>
                )
              })}

            <line className='firstSectionLine' x1='0' y1='60' x2='0' y2='600' />
          </g>
        </g>

        <g transform='translate(0, 50)'>
          <Acceptors acceptors={acceptors} />
        </g>
      </svg>
    </div>
  )
}

const Acceptors: React.FC<{ acceptors: Immutable.Iterable<number, IAcceptorRecord> }> = ({
  acceptors,
}) => {
  const acceptorsWithTime = acceptors.filter(a => !!a && a.sectionalTime != null)
  const times = acceptorsWithTime.map(
    (a: IAcceptorRecord) => a.sectionalTime
  ) as Immutable.Iterable<number, number>
  const maxTime = times.max()
  const minTime = times.min()

  return (
    <g>
      <g transform='translate(-645, 0) scale(.6)'>
        <polygon className='jumpLabelBackground' points={jumpBgPoints} />
        <text className='jumpLabelText' transform='translate(48,78)' textAnchor='middle'>
          Jump
        </text>
      </g>

      {acceptorsWithTime
        .valueSeq()
        .toArray()
        .map((acceptor: IAcceptorRecord, key) => {
          const top = laneHeight * (acceptor.startingPosition - 1)
          return (
            <g
              key={key}
              data-tid-acceptor-row={acceptor.acceptorNumber}
              transform={`translate(0, ${top})`}
            >
              {acceptor.isGiddyupScratched ? (
                <ScratchedRow acceptor={acceptor} />
              ) : (
                <AcceptorRow
                  key={acceptor.startingPosition}
                  acceptor={acceptor}
                  sectionalPosition={sectionalPosition(
                    maxTime,
                    minTime,
                    /* we know this is a number because we have filtered out acceptors where it's not */
                    acceptor.sectionalTime as number
                  )}
                />
              )}
            </g>
          )
        })}
    </g>
  )
}

const ScratchedRow: React.FC<{ acceptor: IAcceptorRecord }> = ({ acceptor }) => {
  const scrBoxWidth = 40
  const extraInfo = getAcceptor(acceptor.key, acceptor.acceptorNumber)
  return (
    <g>
      <line className='laneLine' x1='-720' x2='100' y1='120' y2='120' />

      <g transform={`translate(${viewX}, 70) scale(.4)`}>
        <DogColourGroup acceptorNumber={acceptor.acceptorNumber} border={false} />
      </g>

      <g data-tid-acceptorname transform={`translate(${-adjustedViewWidth + 5}, ${nameY})`}>
        <rect className='scratchedBox' x='0' y='-16' width={scrBoxWidth} height='20' />

        <text className='scratchedText' x='3' y='0' fontSize='16'>
          SCR
        </text>

        <text style={{ fill: '#fff' }} x={scrBoxWidth + 5} y='0' fontSize='18'>
          {extraInfo && extraInfo.name}
        </text>
      </g>
    </g>
  )
}

const AcceptorRow: React.FC<{
  acceptor: IAcceptorRecord
  sectionalPosition: number
}> = ({ acceptor, sectionalPosition }) => {
  const right = sectionalPosition * (adjustedViewWidth - dogWidth)
  return (
    <g>
      {acceptor.isGiddyupScratched ? null : (
        <g>
          <line className='laneLine' x1='-720' x2='100' y1='120' y2='120' />
          <g transform={`translate(${viewX}, 70) scale(.4)`}>
            <DogColourGroup acceptorNumber={acceptor.acceptorNumber} border={false} />

            <g transform='translate(100)'>
              {acceptor.jumpDirection in jumpPaths ? (
                <g>
                  <rect className='jumpDirectionBackground' width='100' height='100' />
                  <path
                    className='jumpDirectionArrow'
                    transform='translate(0,12.5) scale(.75)'
                    d={jumpPaths[acceptor.jumpDirection]}
                  />
                </g>
              ) : (
                <g />
              )}
            </g>
          </g>

          <g
            data-tid-acceptor-glyph={acceptor.acceptorNumber}
            transform={`translate(${-right}, -29)`}
          >
            <g transform='matrix(1.4, 0,0,1.4,-140, 0)'>
              <ellipse className='shadow' cx='53' cy='106' rx='45' ry='5' />

              <Suspense fallback={null}>
                <DogPath className='dog' />
              </Suspense>

              <g transform='translate(23,28) scale(.65)'>
                <path
                  className='acceptorNumberChevron'
                  transform='translate(26 20)'
                  d={chevronPath}
                />
                <text
                  data-tid-acceptor-number={acceptor.acceptorNumber}
                  className='acceptorNumberText'
                  transform='translate(56 86)'
                >
                  {acceptor.acceptorNumber}
                </text>
              </g>
            </g>
          </g>

          <g transform='translate(32.5, 105)'>
            {acceptor.preferredTrackPosition in runLines ? (
              <text className='runLineText'>{runLines[acceptor.preferredTrackPosition]}</text>
            ) : (
              <g />
            )}
          </g>
        </g>
      )}
      <g data-tid-acceptorname>
        <DogName acceptor={acceptor} right={right} />
      </g>
    </g>
  )
}

const DogName: React.FC<{ acceptor: IAcceptorRecord; right: number }> = ({ acceptor, right }) => {
  const extraInfo = getAcceptor(acceptor.key, acceptor.acceptorNumber)

  if (!acceptor || !extraInfo) {
    return (
      <g transform={`translate(${-adjustedViewWidth + 5}, ${nameY})`}>
        <text style={{ fill: '#fff' }} x='0' y='0' fontSize='18'>
          *** NO DATA AVAILABLE ***
        </text>
      </g>
    )
  }

  if (isVacant(acceptor.sectionalTime)) {
    return (
      <g transform={`translate(${-adjustedViewWidth + 5}, ${nameY})`}>
        <text style={{ fill: '#fff' }} x='0' y='0' fontSize='18' data-tid-vacant>
          {extraInfo.name}
        </text>
      </g>
    )
  }

  // this is the distance of the dog from the left hand side of the view
  const left = adjustedViewWidth - (right + dogWidth)
  const plotLeft = left >= right

  const offset = 5
  const x = plotLeft ? -right - dogWidth - offset : -right + offset
  const textAnchor = plotLeft ? 'end' : 'start'
  return (
    <text style={{ fill: '#fff' }} x={x} y={nameY} textAnchor={textAnchor} fontSize='18'>
      {getFormattedName(extraInfo.name)}
    </text>
  )
}

// =============
// Local Helpers
// =============

const jumpPaths: Record<string, string> = {
  LEFT: jumpLeftPath,
  STRAIGHT: jumpStraightPath,
  RIGHT: jumpRightPath,
}

const runLines: Record<string, string> = { RAIL: 'rail', MIDDLE: 'mid', WIDE: 'wide' }

function getFormattedName(name: string) {
  const maxCharacters = 15
  const ellipsis = '\u2026'
  return name.length <= maxCharacters ? name : `${name.substr(0, maxCharacters)}${ellipsis}`
}

function isVacant(sectionalTime: number | null | undefined) {
  return !sectionalTime || sectionalTime <= 0
}

function sectionalPosition(maxTime: number, minTime: number, acceptorTime: number) {
  if (maxTime - minTime <= 0) {
    return 0
  }
  return (acceptorTime - minTime) / (maxTime - minTime)
}
